'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import PublicNavbar from './PublicNavbar';
import PublicFooter from './PublicFooter';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';

interface LiveOrder {
  id: string;
  pickup_city: string;
  destination_city: string;
  parcel_category: string;
  weight_kg: number;
  offered_price_kes: number;
  status: string;
  created_at: string;
}

const stats = [
  { value: '4,820+', label: 'Verified Travelers', icon: '🛡️' },
  { value: '31,400+', label: 'Deliveries Done', icon: '📦' },
  { value: '47', label: 'Counties Covered', icon: '🗺️' },
  { value: '4.87★', label: 'Average Rating', icon: '⭐' },
];

const deliveryFeatures = [
  {
    title: 'Verified Traveler Network',
    desc: 'Every courier is a real traveler with a verified ID, phone number, guarantor on file, and identity badge — not a stranger.',
    icon: '🛡️',
    color: 'from-emerald-500/20 to-emerald-600/5',
    accent: '#16A34A',
    border: 'border-emerald-100',
  },
  {
    title: 'Escrow-Protected Payments',
    desc: 'Funds are held securely until your parcel is confirmed delivered. Zero risk for senders. M-Pesa, Bank Transfer, and Card supported.',
    icon: '🔐',
    color: 'from-amber-500/20 to-amber-600/5',
    accent: '#F59E0B',
    border: 'border-amber-100',
  },
  {
    title: 'Live GPS Tracking',
    desc: 'Real-time location updates, route progress, QR pickup verification, and delivery PIN confirmation — every step visible.',
    icon: '📍',
    color: 'from-sky-500/20 to-sky-600/5',
    accent: '#38BDF8',
    border: 'border-sky-100',
  },
  {
    title: 'Earn While You Travel',
    desc: 'Travelers earn KES 3,000–5,000 extra per trip on routes they\'re already taking. No extra trips needed.',
    icon: '💰',
    color: 'from-emerald-500/20 to-emerald-600/5',
    accent: '#16A34A',
    border: 'border-emerald-100',
  },
];

const storageFeatures = [
  { icon: '📹', label: 'CCTV Monitored', desc: '24/7 surveillance' },
  { icon: '🔒', label: 'Access Controlled', desc: 'PIN & key entry' },
  { icon: '🛡️', label: 'Insured Storage', desc: 'Item protection' },
  { icon: '🏢', label: 'Verified Hosts', desc: 'Admin approved' },
  { icon: '📱', label: 'Instant Booking', desc: 'Book in minutes' },
  { icon: '💬', label: 'Host Chat', desc: 'Direct messaging' },
];

const steps = [
  { step: '01', title: 'Post Your Parcel', desc: 'Describe your package, set your route, and suggest a fair price. Takes under 2 minutes.', role: 'sender' },
  { step: '02', title: 'Travelers Bid', desc: 'Verified travelers on matching routes place competitive bids. Review profiles, ratings, and guarantors.', role: 'traveler' },
  { step: '03', title: 'Secure & Ship', desc: 'Payment goes into escrow. QR code pickup verification. Live GPS tracking begins.', role: 'both' },
  { step: '04', title: 'Delivered & Paid', desc: 'PIN-verified delivery. Escrow released. Both parties rate each other.', role: 'both' },
];

const testimonials = [
  {
    quote: 'Sent my laptop from Nairobi to Mombasa in 6 hours. The traveler was professional, the tracking was spot-on, and the guarantor system gave me total confidence.',
    name: 'Amina K.',
    role: 'Sender · Nairobi',
    initials: 'AK',
    color: 'bg-emerald-600',
  },
  {
    quote: 'I travel Nairobi–Kisumu every week for work. MuyuRide lets me earn KES 3,000–5,000 extra per trip without any hassle. The escrow system is seamless.',
    name: 'Brian O.',
    role: 'Traveler · Kisumu',
    initials: 'BO',
    color: 'bg-amber-500',
  },
  {
    quote: 'Stored my luggage at a verified MuyuRide facility near Nairobi CBD for 3 days. CCTV, access control, and a responsive host. Exactly what I needed.',
    name: 'Fatuma M.',
    role: 'Storage Customer · Mombasa',
    initials: 'FM',
    color: 'bg-sky-500',
  },
];

const trustBadges = [
  { label: 'ID Verified Travelers', icon: '✓' },
  { label: 'Guarantor on Every Trip', icon: '✓' },
  { label: 'Escrow Protected', icon: '✓' },
  { label: 'Real-Time Tracking', icon: '✓' },
  { label: 'AI Price Suggestions', icon: '✓' },
  { label: 'All 47 Counties', icon: '✓' },
  { label: '1.5% Insurance on All Orders', icon: '✓' },
];

// Animated route line SVG
function AnimatedRouteLine() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="routeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#16A34A" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.4" />
        </linearGradient>
      </defs>
      {/* Route lines */}
      <path d="M 50 300 Q 200 100 400 200 Q 600 300 750 150" stroke="url(#routeGrad)" strokeWidth="2" fill="none" strokeDasharray="8 4">
        <animate attributeName="stroke-dashoffset" from="0" to="-48" dur="3s" repeatCount="indefinite" />
      </path>
      <path d="M 100 450 Q 300 350 500 400 Q 650 430 780 350" stroke="url(#routeGrad)" strokeWidth="1.5" fill="none" strokeDasharray="6 3">
        <animate attributeName="stroke-dashoffset" from="0" to="-36" dur="4s" repeatCount="indefinite" />
      </path>
      {/* Location pins */}
      <circle cx="50" cy="300" r="5" fill="#16A34A" opacity="0.8">
        <animate attributeName="r" values="5;7;5" dur="2s" repeatCount="indefinite" />
      </circle>
      <circle cx="400" cy="200" r="4" fill="#38BDF8" opacity="0.8">
        <animate attributeName="r" values="4;6;4" dur="2.5s" repeatCount="indefinite" />
      </circle>
      <circle cx="750" cy="150" r="5" fill="#F59E0B" opacity="0.8">
        <animate attributeName="r" values="5;7;5" dur="3s" repeatCount="indefinite" />
      </circle>
      {/* Package icon moving along route */}
      <rect x="0" y="0" width="12" height="10" rx="2" fill="#16A34A" opacity="0.7">
        <animateMotion dur="6s" repeatCount="indefinite" path="M 50 300 Q 200 100 400 200 Q 600 300 750 150" />
      </rect>
    </svg>
  );
}

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          let start = 0;
          const duration = 1800;
          const step = (timestamp: number) => {
            if (!start) start = timestamp;
            const progress = Math.min((timestamp - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function LandingPageContent() {
  const [liveOrders, setLiveOrders] = useState<LiveOrder[]>([]);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('orders')
      .select('id, pickup_city, destination_city, parcel_category, weight_kg, offered_price_kes, status, created_at')
      .in('status', ['open', 'matched', 'in_transit', 'delivered', 'completed'])
      .order('created_at', { ascending: false })
      .limit(6)
      .then(({ data }) => {
        if (data) setLiveOrders(data);
      });
  }, []);

  const getStatusColor = (status: string) => {
    const map: Record<string, string> = {
      open: 'bg-blue-100 text-blue-700',
      matched: 'bg-amber-100 text-amber-700',
      in_transit: 'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  const getStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      open: 'Seeking Traveler',
      matched: 'Traveler Matched',
      in_transit: 'In Transit',
      delivered: 'Delivered',
      completed: 'Completed',
    };
    return map[status] || status;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden">
      <PublicNavbar />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Deep Navy background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#0F2A1A] to-[#0F172A]" />
        {/* Animated blobs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-[#16A34A]/20 blur-[120px] animate-[pulse_6s_ease-in-out_infinite]" />
          <div className="absolute top-1/2 -right-40 w-[500px] h-[500px] rounded-full bg-[#F59E0B]/10 blur-[100px] animate-[pulse_8s_ease-in-out_infinite_2s]" />
          <div className="absolute -bottom-20 left-1/3 w-[400px] h-[400px] rounded-full bg-[#38BDF8]/10 blur-[80px] animate-[pulse_7s_ease-in-out_infinite_1s]" />
          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
          {/* Animated route lines */}
          <AnimatedRouteLine />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left content */}
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#16A34A]/15 border border-[#16A34A]/30 text-green-300 text-sm font-semibold">
                <div className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
                Now live across all 47 Kenyan counties
              </div>

              {/* Logo + Brand */}
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center p-2 backdrop-blur-sm">
                  <Image
                    src="/assets/images/muyuride_logo.png"
                    alt="MuyuRide Logo — Kenya's Trusted Peer-to-Peer Travel Marketplace"
                    width={52}
                    height={52}
                    className="object-contain"
                    priority
                  />
                </div>
                <div>
                  <p className="text-white font-extrabold text-xl tracking-tight">MuyuRide</p>
                  <p className="text-green-300/80 text-xs font-medium">Kenya's P2P Delivery Network</p>
                </div>
              </div>

              <div className="space-y-4">
                <h1 className="font-heading text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[1.05] tracking-tight">
                  Kenya&apos;s Trusted
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#16A34A] via-[#4ADE80] to-[#38BDF8]">
                    Peer-to-Peer
                  </span>
                  <br />
                  Marketplace.
                </h1>
                <p className="text-lg lg:text-xl text-slate-300 leading-relaxed max-w-lg font-sans">
                  Send parcels with verified travelers already heading your way. Store luggage at secure, verified facilities. Faster, cheaper, and safer than traditional courier services.
                </p>
                {/* Slogan */}
                <p className="text-sm text-green-300/80 font-semibold italic">
                  &ldquo;Every Traveler is a Courier. Every Route is a Delivery.&rdquo;
                </p>
              </div>

              {/* Dual CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  href="/sign-up-login-screen"
                  className="group flex items-center justify-center gap-2.5 px-7 py-4 rounded-2xl bg-[#16A34A] text-white font-heading font-bold text-base hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_24px_rgba(22,163,74,0.45)] hover:shadow-[0_8px_32px_rgba(22,163,74,0.55)] hover:-translate-y-1"
                >
                  📦 Send a Parcel
                  <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  href="/storage-listings"
                  className="flex items-center justify-center gap-2.5 px-7 py-4 rounded-2xl bg-white/8 border border-white/20 text-white font-heading font-bold text-base hover:bg-white/12 hover:border-white/30 transition-all duration-300 backdrop-blur-sm"
                >
                  🏢 Find Storage
                </Link>
              </div>

              {/* Secondary CTA */}
              <div className="flex items-center gap-3">
                <Link href="/sign-up-login-screen" className="text-sm text-slate-400 hover:text-[#F59E0B] transition-colors font-medium">
                  💰 Earn as a Traveler →
                </Link>
                <span className="text-slate-600">·</span>
                <Link href="/sign-up-login-screen" className="text-sm text-slate-400 hover:text-[#F59E0B] transition-colors font-medium">
                  🏢 List Storage Space →
                </Link>
              </div>

              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {trustBadges.slice(0, 4).map((badge) => (
                  <div key={badge.label} className="flex items-center gap-1.5 text-sm text-slate-400">
                    <svg className="w-4 h-4 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {badge.label}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Bento stats cards */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, i) => (
                <div
                  key={stat.label}
                  className={`relative overflow-hidden rounded-2xl p-6 border border-white/10 backdrop-blur-sm ${
                    i === 0 ? 'bg-gradient-to-br from-[#16A34A]/30 to-[#16A34A]/5' :
                    i === 1 ? 'bg-gradient-to-br from-[#F59E0B]/20 to-[#F59E0B]/5' :
                    i === 2 ? 'bg-gradient-to-br from-[#38BDF8]/20 to-[#38BDF8]/5' : 'bg-gradient-to-br from-white/10 to-white/3'
                  }`}
                >
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-heading font-extrabold text-white tabular-nums">{stat.value}</div>
                  <div className="text-sm text-slate-400 mt-1">{stat.label}</div>
                </div>
              ))}

              {/* Service cards */}
              <div className="col-span-2 grid grid-cols-2 gap-4">
                <div className="rounded-2xl p-5 bg-gradient-to-br from-[#16A34A]/20 to-transparent border border-[#16A34A]/20 backdrop-blur-sm">
                  <div className="text-2xl mb-2">📦</div>
                  <div className="text-sm font-heading font-bold text-white">Parcel Delivery</div>
                  <div className="text-xs text-slate-400 mt-1">Peer-to-peer shipping</div>
                </div>
                <div className="rounded-2xl p-5 bg-gradient-to-br from-[#F59E0B]/20 to-transparent border border-[#F59E0B]/20 backdrop-blur-sm">
                  <div className="text-2xl mb-2">🏢</div>
                  <div className="text-sm font-heading font-bold text-white">Secure Storage</div>
                  <div className="text-xs text-slate-400 mt-1">Verified facilities</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#F8FAFC] to-transparent" />
      </section>

      {/* ── DUAL SERVICE BANNER ── */}
      <section className="py-12 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-5 p-6 rounded-2xl bg-gradient-to-br from-green-50 to-white border border-green-100 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-[#16A34A]/10 flex items-center justify-center text-2xl flex-shrink-0">📦</div>
              <div>
                <h3 className="font-heading text-lg font-extrabold text-[#0F172A] mb-1">Peer-to-Peer Parcel Delivery</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">Send parcels with verified travelers already heading your way. Escrow-protected, GPS-tracked, and guaranteed across all 47 Kenyan counties.</p>
                <div className="flex flex-wrap gap-2">
                  {['ID Verified', 'Guarantor Required', 'Escrow Protected', 'Live Tracking', '1.5% Insured'].map(tag => (
                    <span key={tag} className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${tag === '1.5% Insured' ? 'bg-emerald-100 border-emerald-300 text-emerald-700' : 'bg-green-50 border-green-200 text-green-700'}`}>{tag}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-5 p-6 rounded-2xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 hover:shadow-lg transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-[#F59E0B]/10 flex items-center justify-center text-2xl flex-shrink-0">🏢</div>
              <div>
                <h3 className="font-heading text-lg font-extrabold text-[#0F172A] mb-1">Secure Luggage & Parcel Storage</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">Book verified storage facilities near bus stations, airports, and transport hubs across Kenya. CCTV monitored, access controlled, insured.</p>
                <div className="flex flex-wrap gap-2">
                  {['CCTV Monitored', 'Access Controlled', 'Insured', 'Verified Hosts'].map(tag => (
                    <span key={tag} className="px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE ORDERS FEED ── */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold">
              <span className="w-2 h-2 rounded-full bg-[#16A34A] animate-pulse" />
              Live Platform Activity
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A] tracking-tight">
              Real orders. Right now.
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">See the latest delivery requests posted by real senders across Kenya — the platform is active 24/7.</p>
          </div>

          {liveOrders.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {liveOrders.map((order, i) => (
                <div key={order.id} className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 p-5 overflow-hidden">
                  {/* Subtle gradient accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#16A34A] to-[#38BDF8] rounded-t-2xl" />

                  {/* Route */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-[#0F172A] truncate">{order.pickup_city}</span>
                        <svg className="w-4 h-4 text-[#16A34A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                        <span className="text-sm font-extrabold text-[#0F172A] truncate">{order.destination_city}</span>
                      </div>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-400 mb-0.5">Category</p>
                      <p className="text-xs font-bold text-[#0F172A] capitalize truncate">{order.parcel_category}</p>
                    </div>
                    <div className="text-center p-2 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-400 mb-0.5">Weight</p>
                      <p className="text-xs font-bold text-[#0F172A]">{order.weight_kg} kg</p>
                    </div>
                    <div className="text-center p-2 rounded-xl bg-green-50">
                      <p className="text-xs text-slate-400 mb-0.5">Offer</p>
                      <p className="text-xs font-bold text-[#16A34A]">KES {Number(order.offered_price_kes).toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {timeAgo(order.created_at)}
                    </span>
                    {order.status === 'open' && (
                      <Link href="/sign-up-login-screen" className="text-[11px] font-bold text-[#16A34A] hover:underline">
                        Bid on this →
                      </Link>
                    )}
                  </div>

                  {/* Order number badge */}
                  <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center">
                    <span className="text-[10px] font-bold text-slate-400">#{i + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {[
                { from: 'Nairobi', to: 'Mombasa', cat: 'Electronics', kg: 2.5, price: 1200, status: 'open', ago: '3m ago' },
                { from: 'Kisumu', to: 'Nairobi', cat: 'Documents', kg: 0.5, price: 400, status: 'matched', ago: '12m ago' },
                { from: 'Nakuru', to: 'Eldoret', cat: 'Clothing', kg: 4, price: 800, status: 'in_transit', ago: '1h ago' },
                { from: 'Mombasa', to: 'Malindi', cat: 'Food Items', kg: 3, price: 600, status: 'delivered', ago: '2h ago' },
                { from: 'Nairobi', to: 'Nyeri', cat: 'Household', kg: 6, price: 1500, status: 'open', ago: '5m ago' },
                { from: 'Thika', to: 'Nairobi', cat: 'Parcels', kg: 1.5, price: 350, status: 'completed', ago: '3h ago' },
              ].map((order, i) => (
                <div key={i} className="relative bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 p-5 overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#16A34A] to-[#38BDF8] rounded-t-2xl" />
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-extrabold text-[#0F172A]">{order.from}</span>
                        <svg className="w-4 h-4 text-[#16A34A] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                        <span className="text-sm font-extrabold text-[#0F172A]">{order.to}</span>
                      </div>
                    </div>
                    <span className={`flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="text-center p-2 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-400 mb-0.5">Category</p>
                      <p className="text-xs font-bold text-[#0F172A] truncate">{order.cat}</p>
                    </div>
                    <div className="text-center p-2 rounded-xl bg-slate-50">
                      <p className="text-xs text-slate-400 mb-0.5">Weight</p>
                      <p className="text-xs font-bold text-[#0F172A]">{order.kg} kg</p>
                    </div>
                    <div className="text-center p-2 rounded-xl bg-green-50">
                      <p className="text-xs text-slate-400 mb-0.5">Offer</p>
                      <p className="text-xs font-bold text-[#16A34A]">KES {order.price.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-slate-400">{order.ago}</span>
                    {order.status === 'open' && (
                      <Link href="/sign-up-login-screen" className="text-[11px] font-bold text-[#16A34A] hover:underline">Bid on this →</Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center">
            <Link href="/sign-up-login-screen" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#16A34A] text-white font-heading font-bold text-sm hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_14px_rgba(22,163,74,0.35)]">
              Join & Start Delivering →
            </Link>
          </div>
        </div>
      </section>

      {/* ── DELIVERY FEATURES BENTO (Why MuyuRide) ── */}
      <section className="py-24 lg:py-32 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest">
              Why MuyuRide
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-[#0F172A] tracking-tight">
              Built for trust,<br />
              <span className="text-[#16A34A]">designed for Kenya.</span>
            </h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">
              We didn&apos;t copy a Western model. We built something that works for Kenyan roads, Kenyan travelers, and Kenyan senders — across all 47 counties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            <div className={`md:col-span-7 relative overflow-hidden rounded-3xl p-8 lg:p-10 bg-gradient-to-br ${deliveryFeatures[0].color} border ${deliveryFeatures[0].border} group hover:shadow-[0_8px_40px_rgba(22,163,74,0.15)] transition-all duration-500`}>
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[#16A34A]/8 blur-3xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="text-4xl mb-6">{deliveryFeatures[0].icon}</div>
                <h3 className="font-heading text-2xl font-extrabold text-[#0F172A] mb-3">{deliveryFeatures[0].title}</h3>
                <p className="text-slate-600 leading-relaxed text-base max-w-md">{deliveryFeatures[0].desc}</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {['AK', 'BO', 'FM', 'JN'].map((init, i) => (
                      <div key={init} className={`w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white ${['bg-emerald-600', 'bg-amber-500', 'bg-sky-500', 'bg-emerald-400'][i]}`}>
                        {init}
                      </div>
                    ))}
                  </div>
                  <span className="text-sm text-slate-500 font-medium">4,820+ verified travelers</span>
                </div>
              </div>
            </div>

            <div className={`md:col-span-5 relative overflow-hidden rounded-3xl p-8 bg-gradient-to-br ${deliveryFeatures[1].color} border ${deliveryFeatures[1].border} group hover:shadow-[0_8px_40px_rgba(245,158,11,0.12)] transition-all duration-500`}>
              <div className="text-4xl mb-6">{deliveryFeatures[1].icon}</div>
              <h3 className="font-heading text-xl font-extrabold text-[#0F172A] mb-3">{deliveryFeatures[1].title}</h3>
              <p className="text-slate-600 leading-relaxed">{deliveryFeatures[1].desc}</p>
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F59E0B]/10 border border-[#F59E0B]/20 text-[#D97706] text-sm font-bold">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                M-Pesa · Bank · Card
              </div>
            </div>

            <div className={`md:col-span-5 relative overflow-hidden rounded-3xl p-8 bg-gradient-to-br ${deliveryFeatures[2].color} border ${deliveryFeatures[2].border} group hover:shadow-[0_8px_40px_rgba(56,189,248,0.12)] transition-all duration-500`}>
              <div className="text-4xl mb-6">{deliveryFeatures[2].icon}</div>
              <h3 className="font-heading text-xl font-extrabold text-[#0F172A] mb-3">{deliveryFeatures[2].title}</h3>
              <p className="text-slate-600 leading-relaxed">{deliveryFeatures[2].desc}</p>
              <div className="mt-5 flex items-center gap-2 text-sm text-sky-600 font-semibold">
                <div className="w-2 h-2 rounded-full bg-sky-500 animate-pulse" />
                QR pickup · PIN delivery · Live GPS
              </div>
            </div>

            <div className={`md:col-span-7 relative overflow-hidden rounded-3xl p-8 lg:p-10 bg-gradient-to-br ${deliveryFeatures[3].color} border ${deliveryFeatures[3].border} group hover:shadow-[0_8px_40px_rgba(22,163,74,0.12)] transition-all duration-500`}>
              <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full bg-emerald-500/8 blur-3xl translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="text-4xl mb-6">{deliveryFeatures[3].icon}</div>
                <h3 className="font-heading text-2xl font-extrabold text-[#0F172A] mb-3">{deliveryFeatures[3].title}</h3>
                <p className="text-slate-600 leading-relaxed text-base max-w-md">{deliveryFeatures[3].desc}</p>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {[['KES 3K–5K', 'Per trip avg'], ['0 extra km', 'No detours'], ['Flexible', 'Your schedule']].map(([val, lbl]) => (
                    <div key={lbl} className="bg-white/60 rounded-xl p-3 text-center border border-green-100">
                      <div className="text-sm font-heading font-extrabold text-green-700">{val}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{lbl}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS WITH VISUAL ── */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest">
              The MuyuRide Process
            </span>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A] tracking-tight">
              From post to delivery in 4 steps.
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">Every delivery is tracked, verified, and escrow-protected from start to finish.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: '01', icon: '📝', title: 'Post Your Parcel', desc: 'Describe your package, set your route (any of 280+ routes), and suggest a fair price. Takes under 2 minutes.', color: 'border-green-200 bg-green-50' },
              { step: '02', icon: '🧳', title: 'Travelers Bid', desc: 'Verified travelers on matching routes place competitive bids. Review profiles, ratings, guarantors, and trust scores.', color: 'border-amber-200 bg-amber-50' },
              { step: '03', icon: '🔐', title: 'Secure & Ship', desc: 'Payment goes into escrow. QR code pickup verification. Live GPS tracking begins immediately.', color: 'border-sky-200 bg-sky-50' },
              { step: '04', icon: '✅', title: 'Delivered & Paid', desc: 'PIN-verified delivery. Sender confirms receipt. Escrow released to traveler. Both parties rate each other.', color: 'border-emerald-200 bg-emerald-50' },
            ].map((item, i) => (
              <div key={item.step} className={`relative rounded-2xl p-6 border ${item.color} hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-10 -right-3 z-10 text-slate-300 text-xl">→</div>
                )}
                <div className="text-4xl font-black text-slate-200 mb-3">{item.step}</div>
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-heading font-extrabold text-[#0F172A] mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link href="/sign-up-login-screen" className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-[#16A34A] text-white font-heading font-bold text-base hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_24px_rgba(22,163,74,0.35)] hover:-translate-y-0.5">
              Start Shipping Today →
            </Link>
          </div>
        </div>
      </section>

      {/* ── STORAGE MARKETPLACE SECTION ── */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-[#0F172A] to-[#0F2A1A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#F59E0B]/10 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#16A34A]/8 blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/15 border border-[#F59E0B]/30 text-[#FCD34D] text-sm font-semibold">
                <div className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />
                Secure Storage Network
              </div>
              <div>
                <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-4">
                  Store Your Luggage<br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FCD34D]">
                    Anywhere in Kenya.
                  </span>
                </h2>
                <p className="text-lg text-slate-300 leading-relaxed">
                  Find verified storage facilities near bus stations, airports, and transport hubs. Book by the hour, day, week, or month. Chat directly with hosts. All facilities are admin-approved and CCTV monitored.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {storageFeatures.map((f) => (
                  <div key={f.label} className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10">
                    <span className="text-xl">{f.icon}</span>
                    <div>
                      <div className="text-sm font-heading font-bold text-white">{f.label}</div>
                      <div className="text-xs text-slate-400">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Link
                  href="/storage-listings"
                  className="inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-[#F59E0B] text-[#0F172A] font-heading font-bold text-base hover:bg-[#D97706] transition-all duration-300 shadow-[0_4px_24px_rgba(245,158,11,0.4)] hover:-translate-y-0.5"
                >
                  Browse Storage
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  href="/sign-up-login-screen"
                  className="inline-flex items-center gap-2.5 px-7 py-4 rounded-2xl bg-white/10 border border-white/20 text-white font-heading font-bold text-base hover:bg-white/15 transition-all duration-300"
                >
                  List Your Space
                </Link>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: '120+', label: 'Verified Facilities', icon: '🏢', color: 'from-amber-500/20' },
                  { value: '47', label: 'Counties Available', icon: '🗺️', color: 'from-emerald-500/20' },
                  { value: '4.9★', label: 'Host Rating Avg', icon: '⭐', color: 'from-sky-500/20' },
                  { value: '24/7', label: 'CCTV Coverage', icon: '📹', color: 'from-emerald-500/20' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-2xl p-5 bg-gradient-to-br ${s.color} to-transparent border border-white/10`}>
                    <div className="text-2xl mb-2">{s.icon}</div>
                    <div className="text-2xl font-heading font-extrabold text-white">{s.value}</div>
                    <div className="text-xs text-slate-400 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl p-6 bg-white/5 border border-white/10 space-y-3">
                <div className="text-sm font-heading font-bold text-white mb-4">Storage Types Available</div>
                {[
                  { type: 'Lockers', desc: 'Small items, documents', icon: '🔒' },
                  { type: 'Rooms', desc: 'Luggage, boxes, equipment', icon: '🚪' },
                  { type: 'Warehouses', desc: 'Bulk goods, commercial', icon: '🏭' },
                  { type: 'Vehicle Storage', desc: 'Motorcycles, bicycles', icon: '🏍️' },
                ].map((item) => (
                  <div key={item.type} className="flex items-center gap-3">
                    <span className="text-lg">{item.icon}</span>
                    <div className="flex-1">
                      <span className="text-sm font-semibold text-white">{item.type}</span>
                      <span className="text-xs text-slate-400 ml-2">— {item.desc}</span>
                    </div>
                    <svg className="w-4 h-4 text-[#F59E0B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STUDENT TRAVELER PROGRAM ── */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-sky-50 border-y border-green-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 border border-green-200 text-green-700 text-sm font-bold">
                🎓 Student Traveler Program
              </div>
              <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A] tracking-tight">
                Students Earn While<br />
                <span className="text-[#16A34A]">Travelling Home.</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                University and college students travelling between campuses and home can earn extra income by carrying parcels on their existing journeys. Verified with your student ID — trusted by senders.
              </p>
              <div className="space-y-3">
                {[
                  'Student ID verification with university badge displayed on profile',
                  'Parent or guardian as guarantor — builds instant trust',
                  'Earn KES 1,500–3,000 on every campus-to-home trip',
                  'Build your delivery reputation from your very first trip',
                  'Reduced 6% platform fee for verified students',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#16A34A]/15 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3 h-3 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <Link
                href="/sign-up-login-screen"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl bg-[#16A34A] text-white font-heading font-bold text-sm hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_14px_rgba(22,163,74,0.35)] hover:-translate-y-0.5"
              >
                Join as Student Traveler
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: '🎓', title: 'Student Verified', desc: 'University/college ID badge displayed on profile — senders trust you instantly' },
                { icon: '👨‍👩‍👧', title: 'Guarantor Required', desc: 'Parent or guardian guarantor for every trip — maximum accountability' },
                { icon: '🛡️', title: 'Trust Score', desc: 'Build your delivery reputation from your very first trip on MuyuRide' },
                { icon: '💸', title: 'Instant Earnings', desc: 'Earn KES 1,500–3,000 on routes you already travel every semester' },
              ].map((item) => (
                <div key={item.title} className="p-5 rounded-2xl bg-white border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-3xl mb-3">{item.icon}</div>
                  <div className="text-sm font-heading font-bold text-[#0F172A] mb-1">{item.title}</div>
                  <div className="text-xs text-slate-500">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-24 lg:py-32 bg-gradient-to-b from-[#0F172A] to-[#0F2A1A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#16A34A]/10 blur-[100px]" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#F59E0B]/8 blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#16A34A]/15 border border-[#16A34A]/30 text-green-300 text-sm font-bold uppercase tracking-widest">
              How It Works
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              Ship in 4 simple steps.
            </h2>
            <p className="text-lg text-slate-400 max-w-xl mx-auto">
              From posting your parcel to confirmed delivery — the whole process is transparent, fast, and secure.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <div key={step.step} className="relative group">
                {i < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-px bg-gradient-to-r from-[#16A34A]/40 to-transparent z-10 -translate-x-6" />
                )}
                <div className="relative overflow-hidden rounded-2xl p-6 bg-white/5 border border-white/10 hover:border-[#16A34A]/40 hover:bg-white/8 transition-all duration-300 group-hover:-translate-y-1">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-4xl font-heading font-black text-[#16A34A]/30 leading-none">{step.step}</span>
                    <div className={`w-2 h-2 rounded-full ${step.role === 'sender' ? 'bg-[#16A34A]' : step.role === 'traveler' ? 'bg-[#F59E0B]' : 'bg-slate-400'}`} />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {step.role === 'sender' ? 'Sender' : step.role === 'traveler' ? 'Traveler' : 'Both'}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-2xl bg-[#16A34A] text-white font-heading font-bold text-base hover:bg-[#15803D] transition-all duration-300 shadow-[0_4px_24px_rgba(22,163,74,0.35)] hover:shadow-[0_8px_32px_rgba(22,163,74,0.45)] hover:-translate-y-0.5"
            >
              Start Shipping Today
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      {/* ── PAYMENT METHODS ── */}
      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest mb-3">
              Secure Payments
            </span>
            <h2 className="font-heading text-3xl font-extrabold text-[#0F172A]">Pay the way you prefer.</h2>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto">All payments are held in escrow until delivery is confirmed. Your money is always protected — no pay-before-delivery scams.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { icon: '📱', name: 'M-Pesa', desc: 'Instant mobile money', color: 'from-green-500/15 to-green-500/5 border-green-100', badge: 'Most Popular' },
              { icon: '🏦', name: 'Bank Transfer', desc: 'All Kenyan banks', color: 'from-sky-500/15 to-sky-500/5 border-sky-100', badge: null },
              { icon: '💳', name: 'Debit / Credit Card', desc: 'Visa & Mastercard', color: 'from-slate-500/10 to-slate-500/5 border-slate-100', badge: null },
              { icon: '🔐', name: 'Escrow Protected', desc: 'Released on delivery', color: 'from-amber-500/15 to-amber-500/5 border-amber-100', badge: 'Always On' },
            ].map((method) => (
              <div key={method.name} className={`relative rounded-2xl p-5 bg-gradient-to-br ${method.color} border text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300`}>
                {method.badge && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-[#16A34A] text-white text-[10px] font-bold whitespace-nowrap">{method.badge}</span>
                )}
                <div className="text-3xl mb-3">{method.icon}</div>
                <p className="font-heading font-extrabold text-[#0F172A] text-sm">{method.name}</p>
                <p className="text-xs text-slate-500 mt-1">{method.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LIVE STATS ── */}
      <section className="py-20 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { target: 4820, suffix: '+', label: 'Verified Travelers', color: 'text-[#16A34A]' },
              { target: 31400, suffix: '+', label: 'Parcels Delivered', color: 'text-[#F59E0B]' },
              { target: 47, suffix: '', label: 'Counties Covered', color: 'text-[#38BDF8]' },
              { target: 98, suffix: '%', label: 'On-Time Delivery', color: 'text-[#16A34A]' },
            ].map((item) => (
              <div key={item.label} className="text-center space-y-2">
                <div className={`text-4xl lg:text-5xl font-heading font-black tabular-nums ${item.color}`}>
                  <AnimatedCounter target={item.target} suffix={item.suffix} />
                </div>
                <div className="text-sm text-slate-500 font-medium">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TRUST & VERIFICATION ── */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest">
              Trust & Safety
            </span>
            <h2 className="font-heading text-3xl lg:text-4xl font-extrabold text-[#0F172A] tracking-tight">
              Every transaction is protected.
            </h2>
            <p className="text-slate-500 max-w-xl mx-auto">MuyuRide's multi-layer trust system ensures every delivery is safe, accountable, and transparent.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {trustBadges.map((badge) => (
              <div key={badge.label} className="flex flex-col items-center gap-3 p-5 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-10 h-10 rounded-full bg-[#16A34A]/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#16A34A]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-slate-700 leading-tight">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest">
              Real Stories
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-[#0F172A] tracking-tight">
              Trusted by thousands<br />
              <span className="text-[#16A34A]">across Kenya.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((item, i) => (
              <div
                key={item.name}
                className={`relative overflow-hidden rounded-3xl p-8 border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  i === 1
                    ? 'bg-gradient-to-br from-[#0F172A] to-[#0F2A1A] border-[#16A34A]/30 text-white'
                    : 'bg-white border-slate-100'
                }`}
              >
                {i === 1 && (
                  <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#16A34A]/15 blur-3xl -translate-y-1/2 translate-x-1/2" />
                )}
                <div className="relative">
                  <div className="flex gap-1 mb-5">
                    {[...Array(5)].map((_, si) => (
                      <svg key={si} className="w-4 h-4 text-[#F59E0B]" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className={`text-base leading-relaxed mb-6 ${i === 1 ? 'text-slate-300' : 'text-slate-600'}`}>
                    &ldquo;{item.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full ${item.color} flex items-center justify-center text-white text-sm font-bold`}>
                      {item.initials}
                    </div>
                    <div>
                      <div className={`font-heading font-bold text-sm ${i === 1 ? 'text-white' : 'text-[#0F172A]'}`}>{item.name}</div>
                      <div className={`text-xs ${i === 1 ? 'text-slate-400' : 'text-slate-500'}`}>{item.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ROUTES HIGHLIGHT ── */}
      <section className="py-16 bg-[#F8FAFC] border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 border border-green-200 text-[#16A34A] text-sm font-bold uppercase tracking-widest mb-3">
              Popular Routes
            </span>
            <h2 className="font-heading text-3xl font-extrabold text-[#0F172A]">280+ active delivery routes.</h2>
            <p className="text-slate-500 mt-2 max-w-xl mx-auto">From the busiest corridors to remote county routes — MuyuRide covers all 47 counties of Kenya.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { from: 'Nairobi', to: 'Mombasa', trips: '2,400+/mo', flag: '🚂' },
              { from: 'Nairobi', to: 'Kisumu', trips: '1,800+/mo', flag: '🚌' },
              { from: 'Nairobi', to: 'Nakuru', trips: '1,200+/mo', flag: '🚗' },
              { from: 'Mombasa', to: 'Malindi', trips: '600+/mo', flag: '🛺' },
              { from: 'Nairobi', to: 'Eldoret', trips: '900+/mo', flag: '🚌' },
              { from: 'Kisumu', to: 'Kakamega', trips: '400+/mo', flag: '🚗' },
            ].map((route) => (
              <div key={`${route.from}-${route.to}`} className="rounded-2xl p-4 bg-white border border-slate-100 hover:border-green-200 hover:shadow-md transition-all duration-300 text-center">
                <div className="text-2xl mb-2">{route.flag}</div>
                <div className="text-xs font-extrabold text-[#0F172A]">{route.from}</div>
                <div className="text-xs text-[#16A34A] font-bold my-0.5">↓</div>
                <div className="text-xs font-extrabold text-[#0F172A]">{route.to}</div>
                <div className="text-[10px] text-slate-400 mt-1.5 font-medium">{route.trips}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA BANNER ── */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#16A34A] via-[#15803D] to-[#16A34A]" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#0F172A]/20 blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.06]"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          {/* Logo in CTA */}
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center p-3 backdrop-blur-sm">
              <Image
                src="/assets/images/muyuride_logo.png"
                alt="MuyuRide Logo"
                width={56}
                height={56}
                className="object-contain"
              />
            </div>
          </div>
          <h2 className="font-heading text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
            Ready to ship smarter<br />across Kenya?
          </h2>
          <p className="text-xl text-green-100 max-w-2xl mx-auto italic">
            &ldquo;Every Traveler is a Courier. Every Route is a Delivery.&rdquo;
          </p>
          <p className="text-base text-green-200 max-w-2xl mx-auto">
            Join 4,820+ verified travelers and thousands of senders already using MuyuRide — Kenya&apos;s Trusted Peer-to-Peer Travel Marketplace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl bg-white text-[#16A34A] font-heading font-extrabold text-base hover:bg-green-50 transition-all duration-300 shadow-[0_4px_24px_rgba(0,0,0,0.15)] hover:shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:-translate-y-0.5"
            >
              Create Free Account
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              href="/storage-listings"
              className="inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-2xl bg-white/10 border border-white/30 text-white font-heading font-bold text-base hover:bg-white/15 transition-all duration-300 backdrop-blur-sm"
            >
              🏢 Find Storage Near You
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
